import { useState, useEffect } from "react";
import { Modal, TextInput, Textarea, LoadingOverlay, Group } from "@mantine/core";
import { Button, Alert } from "@xplatform/design-system";
import { IconX } from "@tabler/icons-react";
import { useTranslation } from "@hooks/useTranslation";
import { useCategory } from "@hooks/useCategory";
import type { CategoryViewModel } from "@models/category";
import "./CategoryDialog.scss";

interface CategoryDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    category?: CategoryViewModel;
}

export const CategoryDialog = ({ open, onClose, onSuccess, category }: CategoryDialogProps) => {
    const { t } = useTranslation();
    const { createCategory, updateCategory, isLoading } = useCategory();
    const [name, setName] = useState("");
    const [code, setCode] = useState("");
    const [description, setDescription] = useState("");
    const [error, setError] = useState<string | null>(null);

    const isEditMode = !!category;

    useEffect(() => {
        if (open && category) {
            setName(category.name);
            setCode(category.code);
            setDescription(category.description || "");
        }
    }, [open, category]);

    const handleClose = () => {
        setName("");
        setCode("");
        setDescription("");
        setError(null);
        onClose();
    };

    const handleSubmit = async () => {
        setError(null);

        if (!name.trim()) {
            setError("Category name is required");
            return;
        }

        if (!code.trim()) {
            setError("Category code is required");
            return;
        }

        try {
            if (isEditMode && category) {
                await updateCategory(category.id, {
                    name,
                    code,
                    description: description || undefined,
                });
            } else {
                await createCategory({
                    name,
                    code,
                    description: description || undefined,
                });
            }

            if (onSuccess) {
                onSuccess();
            }

            handleClose();
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : `Failed to ${isEditMode ? "update" : "create"} category`,
            );
        }
    };

    const handleNameChange = (value: string) => {
        setName(value);
        if (!isEditMode) {
            const generatedCode = value
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "_")
                .replace(/^_+|_+$/g, "");
            setCode(generatedCode);
        }
    };

    return (
        <Modal
            opened={open}
            onClose={handleClose}
            title={
                isEditMode
                    ? t("admin.categories.actions.editCategory")
                    : t("admin.categories.actions.createCategory")
            }
            size="90%"
            className="category-dialog"
            closeButtonProps={{ disabled: isLoading, "aria-label": "Close" }}
            portalProps={{ target: '#admin-portal-root' }}
        >
            <LoadingOverlay visible={isLoading} />

            {error && (
                <Alert color="red" className="category-dialog__error" mb="md" icon={<IconX size={16} />}>
                    {error}
                </Alert>
            )}

            <div className="category-dialog__form">
                <TextInput
                    label={t("admin.table.columns.name")}
                    value={name}
                    onChange={(e) => handleNameChange(e.currentTarget.value)}
                    required
                    autoFocus
                />

                <TextInput
                    label={t("admin.table.columns.code")}
                    value={code}
                    onChange={(e) => setCode(e.currentTarget.value)}
                    required
                    description="Unique identifier (auto-generated from name)"
                />

                <Textarea
                    label={t("admin.table.columns.description")}
                    value={description}
                    onChange={(e) => setDescription(e.currentTarget.value)}
                    rows={3}
                />
            </div>

            <Group justify="flex-end" mt="lg" className="category-dialog__actions">
                <Button variant="default" onClick={handleClose} disabled={isLoading}>
                    {t("common.cancel")}
                </Button>
                <Button onClick={handleSubmit} disabled={isLoading}>
                    {t("common.save")}
                </Button>
            </Group>
        </Modal>
    );
};
